import { useContext, useEffect, useState } from 'react';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';

const NOTE_PARTS = [
  'Subjective',
  'Objective & measurements',
  'Assessment',
  'Plan & goals',
  'Flowsheets & exercises',
  'Billing codes',
  'Copying it all into my EMR',
];

// "Request an integration" (COM-471): goes to support email + Slack.
const IntegrationRequestModal = ({ isOpen, toggle, initialEmr = '', onSent }) => {
  const { apiClient } = useContext(GlobalContext);
  const [emr, setEmr] = useState('');
  const [clinicians, setClinicians] = useState('');
  const [goal, setGoal] = useState('');
  const [slowest, setSlowest] = useState('');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setEmr(initialEmr);
      setClinicians('');
      setGoal('');
      setSlowest('');
      setDetails('');
      setError('');
    }
  }, [isOpen, initialEmr]);

  const disabled =
    !emr.trim() || !(parseInt(clinicians, 10) > 0) || !goal.trim() || !slowest;

  const send = async () => {
    setBusy(true);
    const res = await apiClient.requestIntegration({
      emr: emr.trim(),
      clinicians: parseInt(clinicians, 10),
      goal: goal.trim(),
      slowest_part: slowest,
      details: details.trim(),
      source: 'integrations',
    });
    setBusy(false);
    if (res?.error) {
      setError(res.error);
      return;
    }
    onSent?.(emr.trim());
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Request an integration</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="er-n">
            Which EMR do you use? <span className="text-danger">*</span>
          </Label>
          <Input id="er-n" placeholder="e.g. Prompt, Clinicient" value={emr} onChange={(e) => setEmr(e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label for="er-c">
            How many clinicians do you have? <span className="text-danger">*</span>
          </Label>
          <Input id="er-c" inputMode="numeric" value={clinicians}
            onChange={(e) => setClinicians(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))} />
        </FormGroup>
        <FormGroup>
          <Label for="er-w">
            What are you hoping Comprehend will help you with? <span className="text-danger">*</span>
          </Label>
          <Input id="er-w" type="textarea" rows={2} value={goal} onChange={(e) => setGoal(e.target.value)}
            placeholder="e.g. less charting after hours, more consistent notes across the team" />
        </FormGroup>
        <fieldset className="mb-3">
          <legend style={{ fontSize: '0.875rem', fontWeight: 600 }}>
            What part of documenting takes you the most time? <span className="text-danger">*</span>{' '}
            <span className="text-muted font-weight-normal">Pick one.</span>
          </legend>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            {NOTE_PARTS.map((p) => (
              <label key={p} className="d-inline-flex align-items-center mb-0"
                style={{
                  gap: 8, minHeight: 40, padding: '0 14px', borderRadius: 999, cursor: 'pointer',
                  border: `1px solid ${slowest === p ? '#1F8A47' : '#D5D9E2'}`,
                  background: slowest === p ? '#F3FAF5' : '#fff',
                }}>
                <input type="radio" name="noteslow" checked={slowest === p} onChange={() => setSlowest(p)} />
                {p}
              </label>
            ))}
          </div>
        </fieldset>
        <FormGroup>
          <Label for="er-x">
            Anything specific? <span className="text-muted">(optional)</span>
          </Label>
          <Input id="er-x" type="textarea" rows={2} value={details} onChange={(e) => setDetails(e.target.value)}
            placeholder="e.g. a certain template, eval vs daily notes, home exercise programs" />
        </FormGroup>
        {disabled && <p className="text-muted mb-0">Answer the questions marked * to send.</p>}
        {error && <p className="text-danger mb-0">{error}</p>}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle}>Cancel</Button>
        <Button color="success" onClick={send} disabled={disabled || busy}>Send request</Button>
      </ModalFooter>
    </Modal>
  );
};

export default IntegrationRequestModal;
